import { z } from "zod";
import { LocaleSchema } from "./user";
/**
 * Display mode for the guest gallery. Re-declared locally to avoid a circular
 * import with `./wedding.ts`; kept in sync with `GalleryDisplayModeSchema` there.
 */
const GuestGalleryDisplayModeSchema = z.enum(["carousel", "grid"]);
/**
 * Guest photo album — guests upload photos via a QR-coded `/upload/:token`
 * URL; the couple moderates and reveals them on the public site. Three
 * orthogonal toggles control behavior:
 *   1. `website.visibleSections` includes `"guestGallery"` — section slot exists
 *   2. `website.guestAlbum.enabled` — QR/upload URL works
 *   3. `website.guestAlbum.guestViewEnabled` — approved photos visible on public site
 */
export const GuestPhotoStatusSchema = z.enum(["pending", "approved", "rejected"]);
/** A single guest-uploaded photo stored under `weddings/{id}/guestPhotos/{photoId}`. */
export const GuestPhotoSchema = z.object({
    id: z.string(),
    storagePath: z.string(),
    status: GuestPhotoStatusSchema,
    uploaderName: z.string().max(40).nullable().optional(),
    uploadedAt: z.string().datetime(),
    approvedAt: z.string().datetime().nullable().optional(),
    rejectedAt: z.string().datetime().nullable().optional(),
    order: z.number().int().nonnegative().default(0),
});
/** Couple-controlled album settings, persisted under `wedding.website.guestAlbum`. */
export const GuestAlbumConfigSchema = z.object({
    enabled: z.boolean().default(false),
    uploadToken: z.string().min(40),
    moderationEnabled: z.boolean().default(true),
    guestViewEnabled: z.boolean().default(false),
    displayMode: GuestGalleryDisplayModeSchema.default("grid"),
});
/**
 * Public token doc at `/guestUploadTokens/{token}` — the document id IS the
 * secret token. Mirrors the RSVP-token snapshot pattern: the public upload
 * page is unauthenticated and cannot read `/weddings`, so everything it
 * needs to render lives here. Owner/partner (re)writes it whenever the
 * album settings change.
 */
export const GuestUploadTokenSchema = z.object({
    token: z.string(),
    weddingId: z.string(),
    ownerId: z.string(),
    coupleName1: z.string(),
    coupleName2: z.string(),
    weddingDate: z.string().datetime(),
    venueName: z.string().nullable().optional(),
    publicHandle: z.string().nullable().optional(),
    enabled: z.boolean(),
    moderationEnabled: z.boolean(),
    guestViewEnabled: z.boolean(),
    locale: LocaleSchema.default("pl"),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/** Aggregate counts for the builder preview's reveal-off placeholder. */
export const GuestGalleryStatsSchema = z.object({
    photoCount: z.number().int().nonnegative(),
    guestCount: z.number().int().nonnegative(),
    pendingCount: z.number().int().nonnegative(),
    totalUploadCount: z.number().int().nonnegative(),
    lastUpdatedAt: z.string().datetime().nullable(),
});
